// src/theme/theme.ts
import { createTheme, PaletteColor, PaletteColorOptions, ThemeOptions, darken, lighten } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import { red } from '@mui/material/colors';
// Fonts
import '@fontsource/dm-serif-text';
import '@fontsource/inter';


// Custom palette augmentation
declare module '@mui/material/styles' {
  interface Palette {
    moneyGreen: PaletteColor;
  }
  interface PaletteOptions {
    moneyGreen?: PaletteColorOptions;
  }
}

type TenantThemeOverrideInput = {
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  palette?: ThemeOptions['palette'];
  typography?: ThemeOptions['typography'];
  components?: ThemeOptions['components'];
};

const rawOverrides = process.env.REACT_APP_THEME_OVERRIDES;
let tenantThemeOverrides: TenantThemeOverrideInput = {};

if (rawOverrides) {
  try {
    tenantThemeOverrides = JSON.parse(rawOverrides) as TenantThemeOverrideInput;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Invalid REACT_APP_THEME_OVERRIDES value. Expected JSON, received:', rawOverrides, error);
  }
}

const paletteFromShortcuts: NonNullable<ThemeOptions['palette']> = {};

if (tenantThemeOverrides.primaryColor) {
  const main = tenantThemeOverrides.primaryColor;
  const existing = tenantThemeOverrides.palette?.primary ?? {};
  const derived: PaletteColorOptions = {
    ...existing,
    main,
  };

  if (main) {
    if (!derived.light) {
      derived.light = lighten(main, 0.2);
    }

    if (!derived.dark) {
      derived.dark = darken(main, 0.2);
    }
  }

  paletteFromShortcuts.primary = derived;
}

if (tenantThemeOverrides.secondaryColor) {
  const main = tenantThemeOverrides.secondaryColor;
  const existing = tenantThemeOverrides.palette?.secondary ?? {};
  const derived: PaletteColorOptions = {
    ...existing,
    main,
  };

  if (main) {
    if (!derived.light) {
      derived.light = lighten(main, 0.2);
    }

    if (!derived.dark) {
      derived.dark = darken(main, 0.2);
    }
  }

  paletteFromShortcuts.secondary = derived;
}

if (tenantThemeOverrides.backgroundColor) {
  paletteFromShortcuts.background = {
    ...(tenantThemeOverrides.palette?.background ?? {}),
    default: tenantThemeOverrides.backgroundColor,
  };
}

let mergedPaletteOverrides: ThemeOptions['palette'];

if (
  tenantThemeOverrides.palette ||
  Object.keys(paletteFromShortcuts).length > 0
) {
  mergedPaletteOverrides = deepmerge(
    tenantThemeOverrides.palette ?? {},
    paletteFromShortcuts
  );
}

const overrideThemeOptions: ThemeOptions = {};

if (mergedPaletteOverrides) {
  overrideThemeOptions.palette = mergedPaletteOverrides;
}

if (tenantThemeOverrides.typography) {
  overrideThemeOptions.typography = tenantThemeOverrides.typography;
}

if (tenantThemeOverrides.components) {
  overrideThemeOptions.components = tenantThemeOverrides.components;
}

const baseThemeOptions: ThemeOptions = {
  palette: {
    primary: {
      main: '#000',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
      contrastText: '#ffffff',
    },
    moneyGreen: {
      main: '#1b5e20',
      light: '#2e7d32',
      dark: '#0d3b16',
      contrastText: '#ffffff',
    },
    error: {
      main: red.A400,
    },
  },
  typography: {
    fontFamily: `'Quicksand', sans-serif`,
    h1: {
      fontFamily: `'Quicksand', sans-serif`,
      fontWeight: 800,
    },
    h2: {
      fontFamily: `'Quicksand', sans-serif`,
      fontWeight: 800,
    },
    h3: {
      fontFamily: `'Quicksand', sans-serif`,
      fontWeight: 800,
    },
    h4: {
      fontFamily: `'Quicksand', sans-serif`,
      fontWeight: 800,
    },
    h5: {
      fontFamily: `'Quicksand', sans-serif`,
      fontWeight: 800,
    },
    h6: {
      fontFamily: `'Quicksand', sans-serif`,
      fontWeight: 800,
    },
    body1: {
      fontFamily: `'Quicksand', sans-serif`,
    },
    subtitle1: {
      fontFamily: `'Quicksand', sans-serif`,
      fontWeight: 800,
    },
    subtitle2: {
      fontFamily: `'Quicksand', sans-serif`,
      fontWeight: 800,
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@font-face': [
          {
            fontFamily: 'Quicksand',
            fontStyle: 'normal',
            fontDisplay: 'swap',
            fontWeight: 300,
            src: "url('/fonts/Quicksand/static/Quicksand-Light.ttf') format('truetype')",
          },
          {
            fontFamily: 'Quicksand',
            fontStyle: 'normal',
            fontDisplay: 'swap',
            fontWeight: 400,
            src: "url('/fonts/Quicksand/static/Quicksand-Regular.ttf') format('truetype')",
          },
          {
            fontFamily: 'Quicksand',
            fontStyle: 'normal',
            fontDisplay: 'swap',
            fontWeight: 500,
            src: "url('/fonts/Quicksand/static/Quicksand-Medium.ttf') format('truetype')",
          },
          {
            fontFamily: 'Quicksand',
            fontStyle: 'normal',
            fontDisplay: 'swap',
            fontWeight: 600,
            src: "url('/fonts/Quicksand/static/Quicksand-SemiBold.ttf') format('truetype')",
          },
          {
            fontFamily: 'Quicksand',
            fontStyle: 'normal',
            fontDisplay: 'swap',
            fontWeight: 700,
            src: "url('/fonts/Quicksand/static/Quicksand-Bold.ttf') format('truetype')",
          },
        ],
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: "lightgray"
          }
        }
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const baseStyles = {
            borderRadius: '20px',
            padding: '8px 16px',
          };

          if (ownerState.variant !== 'contained') {
            return baseStyles;
          }

          const paletteKey =
            ownerState.color && ownerState.color !== 'inherit'
              ? ownerState.color
              : 'primary';

          const paletteEntry = theme.palette[
            paletteKey as keyof typeof theme.palette
          ] as PaletteColor | undefined;

          const backgroundColor = paletteEntry?.main;

          if (!backgroundColor) {
            return baseStyles;
          }

          return {
            ...baseStyles,
            color: paletteEntry?.contrastText ?? theme.palette.getContrastText(backgroundColor),
          };
        },
        contained: {
          '&.Mui-selected': {
            backgroundColor: '#808080',
          },
        },
        outlined: ({ ownerState, theme }) => {
          const paletteKey =
            ownerState.color && ownerState.color !== 'inherit'
              ? ownerState.color
              : 'primary';

          const paletteEntry = theme.palette[
            paletteKey as keyof typeof theme.palette
          ] as PaletteColor | undefined;

          const mainColor = paletteEntry?.main ?? theme.palette.primary.main;

          return {
            borderColor: mainColor,
            color: paletteEntry?.main ?? theme.palette.text.primary,
            '&:hover': {
              borderColor: paletteEntry?.dark ?? darken(mainColor, 0.2),
            },
          };
        },
      },
    },
  },
};

const theme = createTheme(deepmerge(baseThemeOptions, overrideThemeOptions));

export default theme;
